import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  IActivo,
  ICertificadoEntidad,
  ICreateCertificadoEntidad,
  IDispositivoAlarma,
  IQueryParam,
  ITracker,
  IUpdateCertificadoEntidad,
} from 'modelos/src';
import { Subscription, Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { CertificadoEntidadService } from '../../../../auxiliares/servicios/http/certificado-entidad.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { CommonModule } from '@angular/common';
import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import { ParamsService } from '../../../../auxiliares/servicios/params.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { DatetimePickerComponent } from '../../../../auxiliares/datetime-picker/datetime-picker.component';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';
import {
  CrearCertificadoManualComponent,
  ICrearCertificadoManualData,
} from '../crear-certificado-manual/crear-certificado-manual.component';

export interface IParamCertificadoEntidad {
  idEntidad?: string;
  crear?: 'Dialog' | 'Page';
  botonCrear?: boolean;
  [key: string]: any;
}

@Component({
  imports: [CommonModule, AuxiliaresModule, DatetimePickerComponent],
  selector: 'app-crear-editar-certificado-entidad',
  templateUrl: './crear-editar-certificado-entidad.component.html',
  styleUrls: ['./crear-editar-certificado-entidad.component.scss'],
})
export class CrearEditarCertificadoEntidadComponent
  implements OnInit, OnDestroy
{
  dialogData = inject<{
    edit: ICertificadoEntidad;
    params: IParamCertificadoEntidad;
  }>(MAT_DIALOG_DATA, { optional: true });
  dialogRef = inject<MatDialogRef<CrearEditarCertificadoEntidadComponent>>(
    MatDialogRef,
    { optional: true },
  );
  private service = inject(CertificadoEntidadService);
  private lista2 = inject(Lista2Service);
  private paramsService = inject(ParamsService);
  helper = inject(HelperService);
  private formularios = inject(FormularioDialogService);
  route = inject(ActivatedRoute);

  public loading = false;
  public form?: FormGroup;
  public title?: string;
  public tipoEntidadSeleccionado: 'vehiculo' | 'alarma' = 'vehiculo';
  public creandoManual = false;

  // Datos de input al componente
  @Input() public edit?: ICertificadoEntidad;
  @Input() public params?: IParamCertificadoEntidad = {};

  // Entidades
  public vehiculos: IActivo[] = [];
  public alarmas: IDispositivoAlarma[] = [];

  // Listado Continuo
  public vehiculos$?: Subscription;
  public alarmas$?: Subscription;

  // Search para ng-select
  public loadingVehiculos = false;
  public loadingAlarmas = false;
  public searchTermVehiculos$ = new Subject<string>();
  public searchTermAlarmas$ = new Subject<string>();
  public searchVehiculos$?: any;
  public searchTrackers$?: any;
  public searchAlarmas$?: any;

  private createForm(): void {
    this.title = this.edit?._id
      ? 'Editar Certificado de Entidad'
      : 'Crear Certificado de Entidad';

    this.form = new FormGroup({
      idEntidad: new FormControl(
        this.edit?.idEntidad || this.params?.idEntidad,
        [Validators.required],
      ),
      fechaInicio: new FormControl(
        (this.edit as any)?.fechaInicio
          ? new Date((this.edit as any).fechaInicio)
          : new Date(),
        // Fecha de inicio es opcional, solo requerida para certificados programados
      ),
      fechaEmision: new FormControl(
        this.edit?.fechaEmision ? new Date(this.edit.fechaEmision) : new Date(),
        // Fecha de emisión es opcional, solo requerida para certificados programados
      ),
    });

    // Si hay params con idEntidad, el campo ya está preseleccionado
    if (this.params?.idEntidad) {
      this.form.get('idEntidad')?.setValue(this.params.idEntidad);
    }
  }

  async ngOnInit(): Promise<void> {
    if (this.dialogData) {
      this.edit = this.dialogData?.edit;
      this.params = this.dialogData?.params;
    } else {
      const paramsService = this.paramsService.getParams();
      this.edit = paramsService?.['edit'];
      this.params = paramsService?.['params'];
    }

    this.createForm();
    await Promise.all([this.listarVehiculos(), this.listarAlarmas()]);
  }

  async ngOnDestroy() {
    this.vehiculos$?.unsubscribe();
    this.alarmas$?.unsubscribe();
  }

  public async onSubmit(): Promise<void> {
    // Validar que tenga entidad
    const idEntidad = this.form?.value.idEntidad || this.params?.idEntidad;
    if (!idEntidad) {
      this.helper.notifWarn('Seleccioná una entidad');
      return;
    }

    // Para certificados programados, validar que las fechas estén completas
    if (!this.form?.value.fechaInicio || !this.form?.value.fechaEmision) {
      this.helper.notifWarn(
        'Completá las fechas de inicio y fin para crear un certificado programado',
      );
      return;
    }

    // Validar fechas antes de enviar
    const fechaInicio = this.form.value.fechaInicio;
    const fechaEmision = this.form.value.fechaEmision;

    if (fechaEmision < fechaInicio) {
      this.helper.notifWarn(
        'La fecha de fin no puede ser anterior a la fecha de inicio',
      );
      return;
    }

    const diferenciaMs = fechaEmision.getTime() - fechaInicio.getTime();
    const diferenciaMinutos = diferenciaMs / (1000 * 60);

    if (diferenciaMinutos < 5) {
      this.helper.notifWarn(
        'La fecha de fin debe ser al menos 5 minutos posterior a la fecha de inicio',
      );
      return;
    }

    this.loading = true;
    try {
      const idEntidad = this.form.value.idEntidad || this.params?.idEntidad;

      const dato: ICreateCertificadoEntidad = {
        idEntidad: idEntidad,
        fechaEmision: this.form.value.fechaEmision, // Fecha de vencimiento
        ['fechaInicio' as any]: this.form.value.fechaInicio, // Fecha de inicio (para backend)
      };
      await this.service.create(dato);
      this.helper.notifSuccess('Certificado creado correctamente');

      this.loading = false;
      if (this.dialogRef) {
        this.dialogRef.close(true);
      }
    } catch (error) {
      this.loading = false;
      this.helper.notifError(error);
    }
  }

  public cancelar(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  public async crearManualmente(): Promise<void> {
    // Prevenir múltiples ejecuciones
    if (this.creandoManual) {
      return;
    }

    // Para certificado manual solo validamos que haya una entidad seleccionada
    const idEntidad = this.form?.value.idEntidad || this.params?.idEntidad;

    if (!idEntidad) {
      this.helper.notifWarn('Seleccioná una entidad');
      return;
    }

    this.creandoManual = true;
    this.loading = true;
    try {
      // Crear certificado vacío sin fechas (se crean automáticamente en el backend)
      const dato: ICreateCertificadoEntidad = {
        idEntidad: idEntidad,
        // Las fechas se ignoran para certificados manuales
      };

      const certificadoCreado = await this.service.create(dato);
      this.loading = false;

      // Abrir diálogo de creación manual
      const dialogData: ICrearCertificadoManualData = {
        certificadoId: certificadoCreado._id,
      };

      const dialogRef = this.formularios.abrir(
        CrearCertificadoManualComponent,
        {
          tamano: 'mediano',
          data: dialogData,
        },
      );

      const result = await dialogRef.afterClosed().toPromise();
      if (result) {
        this.helper.notifSuccess('Certificado manual creado correctamente');
        if (this.dialogRef) {
          this.dialogRef.close(true);
        }
      }
    } catch (error) {
      this.loading = false;
      this.helper.notifError(error);
    } finally {
      this.creandoManual = false;
    }
  }

  // Listados
  private async listarVehiculos(): Promise<void> {
    try {
      const query: IQueryParam = {
        select: 'identificacion',
        sort: 'identificacion',
        limit: 100,
        filter: JSON.stringify({ tipo: 'Vehiculo' }),
      };
      this.vehiculos$?.unsubscribe();
      this.vehiculos$ = this.lista2.activos(query, 30).subscribe((data) => {
        this.vehiculos = data.datos;
      });
    } catch (error) {
      console.error('Error cargando vehículos:', error);
      this.vehiculos = [];
    }
  }

  private async listarAlarmas(): Promise<void> {
    try {
      const query: IQueryParam = {
        select: 'nombre',
        sort: 'nombre',
        limit: 100,
      };
      this.alarmas$?.unsubscribe();
      this.alarmas$ = this.lista2
        .dispositivosAlarmas(query, 30)
        .subscribe((data) => {
          this.alarmas = data.datos;
        });
    } catch (error) {
      console.error('Error cargando alarmas:', error);
      this.alarmas = [];
    }
  }

  // Search functions para ng-select
  public searchVehiculos(term: string): void {
    this.loadingVehiculos = true;
    const query: IQueryParam = {
      select: 'identificacion',
      sort: 'identificacion',
      limit: 50,
      search: term,
      filter: JSON.stringify({ tipo: 'Vehiculo' }),
    };
    this.searchVehiculos$ = this.lista2.activos(query, 0);
    this.loadingVehiculos = false;
  }

  public searchAlarmas(term: string): void {
    this.loadingAlarmas = true;
    const query: IQueryParam = {
      select: 'nombre',
      sort: 'nombre',
      limit: 50,
      search: term,
    };
    this.searchAlarmas$ = this.lista2.dispositivosAlarmas(query, 0);
    this.loadingAlarmas = false;
  }

  public onFechaInicioChange(date: string): void {
    const fechaInicio = new Date(date);
    this.form?.get('fechaInicio')?.setValue(fechaInicio);

    // Validar si ya existe fechaEmision
    const fechaEmision = this.form?.get('fechaEmision')?.value;
    if (fechaEmision) {
      this.validateFechas(fechaInicio, fechaEmision);
    }
  }

  public onFechaEmisionChange(date: string): void {
    const fechaEmision = new Date(date);
    this.form?.get('fechaEmision')?.setValue(fechaEmision);

    // Validar con fechaInicio
    const fechaInicio = this.form?.get('fechaInicio')?.value;
    if (fechaInicio) {
      this.validateFechas(fechaInicio, fechaEmision);
    }
  }

  private validateFechas(fechaInicio: Date, fechaEmision: Date): void {
    // Validar que la fecha de fin no sea anterior a la fecha de inicio
    if (fechaEmision < fechaInicio) {
      this.helper.notifWarn(
        'La fecha de fin no puede ser anterior a la fecha de inicio',
      );
      return;
    }

    // Calcular diferencia en minutos
    const diferenciaMs = fechaEmision.getTime() - fechaInicio.getTime();
    const diferenciaMinutos = diferenciaMs / (1000 * 60);

    // Validar que haya al menos 5 minutos de diferencia
    if (diferenciaMinutos < 5) {
      this.helper.notifWarn(
        'La fecha de fin debe ser al menos 5 minutos posterior a la fecha de inicio',
      );
      return;
    }
  }
}
